// Chat platform v2 API client (Epic #393 / Sprint 4 / issue #419).
//
// Thin axios wrappers around the new `/chats/...` REST surface. The shared
// axios instance in `helper/api_helper.ts` already handles JWT injection and
// 401-driven refresh, so we use the bare axios import here and let the
// interceptors do their job.

import axios, { AxiosResponse } from 'axios';

import * as url from 'src/helper/url_helpter';

import type {
  ArchiveBody,
  BroadcastSendBody,
  BroadcastSendResult,
  BroadcastTarget,
  Chat,
  ChatAttachment,
  ChatInboxPage,
  ChatMessage,
  ChatMessagesPage,
  MarkReadBody,
  PinMessageBody,
  SendMessageBody,
  SupportAssignBody,
  SupportNoteBody,
  SupportStatusBody,
} from 'src/models/chat';

// Unwrap `{ data, meta }` envelopes the controllers always wrap responses in.
// Returning the raw axios payload would force every hook to reach into
// `response.data.data`, which is noisy and easy to typo.
interface Envelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export interface MyChatsFilter {
  type?: string;
  jobId?: number;
}

export async function getMyChats(
  filter: MyChatsFilter = {}
): Promise<ChatInboxPage> {
  const params: Record<string, string | number> = {};
  if (filter.type) params.type = filter.type;
  if (filter.jobId) params.jobId = filter.jobId;
  const response: AxiosResponse<Envelope<Chat[]>> = await axios.get(
    url.MY_CHATS,
    { params }
  );
  return {
    data: response.data?.data ?? [],
    meta: response.data?.meta ?? {},
  };
}

export async function getJobChats(jobId: number): Promise<Chat[]> {
  const response: AxiosResponse<Envelope<Chat[]>> = await axios.get(
    url.JOB_CHATS(jobId)
  );
  return response.data?.data ?? [];
}

/**
 * `GET /chats/:chatId` returns an enriched envelope — not a bare `Chat`.
 * The controller wraps the row in `{ chat, lastMessagePreview, ... }` via
 * `enrichThread` (see
 * `backend/src/test/integration/chats-rest-surface.spec.ts` — the body is
 * `{ data: { chat, lastMessagePreview } }`). We surface that envelope so
 * callers can read both the chat and its side fields without reaching into
 * an untyped `any`.
 */
export interface ChatDetailEnvelope {
  chat: Chat;
  lastMessagePreview?: string | null;
}

export async function getChatDetail(
  chatId: number
): Promise<ChatDetailEnvelope> {
  const response: AxiosResponse<Envelope<ChatDetailEnvelope>> = await axios.get(
    url.CHAT_DETAIL(chatId)
  );
  return response.data?.data as ChatDetailEnvelope;
}

export interface ChatMessagesParams {
  cursor?: number;
  limit?: number;
}

export async function getChatMessages(
  chatId: number,
  params: ChatMessagesParams = {},
  scope: SupportInboxScope = 'legacy'
): Promise<ChatMessagesPage> {
  const query: Record<string, number> = {};
  if (params.cursor) query.cursor = params.cursor;
  if (params.limit) query.limit = params.limit;
  const response: AxiosResponse<ChatMessagesPage> = await axios.get(
    scope === 'platform' ? url.PLATFORM_SUPPORT_MESSAGES(chatId) : url.CHAT_MESSAGES(chatId),
    { params: query }
  );
  // The REST controller returns the envelope at the top level so the page
  // shape comes back directly.
  return {
    data: response.data?.data ?? [],
    meta: response.data?.meta ?? { nextCursor: null, count: 0 },
  };
}

export async function sendChatMessage(
  chatId: number,
  body: SendMessageBody
): Promise<{ message: ChatMessage }> {
  const response: AxiosResponse<Envelope<{ message: ChatMessage }>> =
    await axios.post(url.CHAT_MESSAGES(chatId), body);
  return response.data?.data ?? { message: undefined as never };
}

export async function markChatRead(
  chatId: number,
  body: MarkReadBody,
  scope: SupportInboxScope = 'legacy'
): Promise<unknown> {
  const response = await axios.post(scope === 'platform' ? `${url.PLATFORM_SUPPORT_DETAIL(chatId)}/read` : url.CHAT_READ(chatId), body);
  return response.data?.data;
}

export async function createLeadDm(jobId: number): Promise<Chat> {
  const response: AxiosResponse<Envelope<Chat>> = await axios.post(
    url.JOB_LEAD_DIRECT(jobId),
    {}
  );
  return response.data?.data as Chat;
}

export async function createContractorDm(
  jobId: number,
  contractorId: number
): Promise<Chat> {
  const response: AxiosResponse<Envelope<Chat>> = await axios.post(
    url.JOB_CONTRACTOR_DIRECT(jobId, contractorId),
    {}
  );
  return response.data?.data as Chat;
}

export async function createJobGroup(jobId: number): Promise<Chat> {
  const response: AxiosResponse<Envelope<Chat>> = await axios.post(
    url.JOB_GROUP_CREATE(jobId),
    {}
  );
  return response.data?.data as Chat;
}

export async function sendBroadcast(
  jobId: number,
  body: BroadcastSendBody
): Promise<BroadcastSendResult> {
  const response: AxiosResponse<Envelope<BroadcastSendResult>> =
    await axios.post(url.JOB_BROADCASTS(jobId), body);
  return response.data?.data as BroadcastSendResult;
}

export interface BroadcastPreviewParams {
  target: BroadcastTarget;
  customUserIds?: number[];
}

/**
 * `GET /chats/jobs/:jobId/broadcasts/preview` — issue #449. Returns the
 * recipient count the corresponding `POST` would deliver to. The server
 * shares its resolver between preview and commit, so this is the canonical
 * count to show the user before they confirm.
 *
 * `customUserIds` is serialized as a comma-separated string; the controller
 * accepts both that form and repeated query keys. Comma-separated is
 * cheaper on the URL when the list is long.
 */
export async function previewBroadcast(
  jobId: number,
  params: BroadcastPreviewParams
): Promise<{ recipientCount: number }> {
  const query: Record<string, string> = { target: params.target };
  if (params.customUserIds && params.customUserIds.length > 0) {
    query.customUserIds = params.customUserIds.join(',');
  }
  const response: AxiosResponse<Envelope<{ recipientCount: number }>> =
    await axios.get(url.JOB_BROADCAST_PREVIEW(jobId), { params: query });
  return response.data?.data ?? { recipientCount: 0 };
}

export async function uploadChatAttachment(
  chatId: number,
  file: File
): Promise<ChatAttachment> {
  const form = new FormData();
  form.append('file', file);
  const response: AxiosResponse<Envelope<ChatAttachment>> = await axios.post(
    url.CHAT_ATTACHMENTS(chatId),
    form,
    {
      headers: { 'content-type': 'multipart/form-data' },
    }
  );
  return response.data?.data as ChatAttachment;
}

export async function archiveChat(
  chatId: number,
  body: ArchiveBody
): Promise<unknown> {
  const response = await axios.patch(url.CHAT_ARCHIVE(chatId), body);
  return response.data?.data;
}

export async function pinMessageV2(
  messageId: number,
  body: PinMessageBody
): Promise<unknown> {
  // Sprint 2 pin endpoint isn't in the new chat-rest.controller — the legacy
  // `/messages/pin-message` route still owns it. We use the new url helper
  // ({MESSAGE_PIN}) for forwards-compatibility; if the backend hasn't moved
  // yet the route is delegated through the existing `pinMessage` helper.
  const response = await axios.patch(url.MESSAGE_PIN(messageId), body);
  return response.data?.data;
}

export async function deleteMessage(messageId: number): Promise<unknown> {
  const response = await axios.delete(url.MESSAGE_DELETE(messageId));
  return response.data?.data;
}

export async function getSupportInbox(
  status?: string,
  scope: SupportInboxScope = 'legacy'
): Promise<{ data: Chat[]; meta: Record<string, unknown> }> {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  const response: AxiosResponse<Envelope<Chat[]>> = await axios.get(
    scope === 'platform' ? url.PLATFORM_SUPPORT_INBOX : url.SUPPORT_INBOX,
    { params }
  );
  return {
    data: response.data?.data ?? [],
    meta: response.data?.meta ?? {},
  };
}

export async function getPlatformSupportTicket(chatId: number): Promise<Chat> {
  const response: AxiosResponse<Envelope<Chat>> = await axios.get(
    url.PLATFORM_SUPPORT_DETAIL(chatId)
  );
  return response.data?.data as Chat;
}

export async function getPlatformSupportMessages(
  chatId: number
): Promise<ChatMessage[]> {
  const response: AxiosResponse<Envelope<ChatMessage[]>> = await axios.get(
    url.PLATFORM_SUPPORT_MESSAGES(chatId)
  );
  return response.data?.data ?? [];
}

export async function assignSupport(
  chatId: number,
  body: SupportAssignBody,
  scope: SupportInboxScope = 'legacy'
): Promise<Chat> {
  const response: AxiosResponse<Envelope<Chat>> = await axios.patch(
    scope === 'platform'
      ? url.PLATFORM_SUPPORT_ASSIGN(chatId)
      : url.SUPPORT_ASSIGN(chatId),
    body
  );
  return response.data?.data as Chat;
}

export async function updateSupportStatus(
  chatId: number,
  body: SupportStatusBody,
  scope: SupportInboxScope = 'legacy'
): Promise<Chat> {
  const response: AxiosResponse<Envelope<Chat>> = await axios.patch(
    scope === 'platform'
      ? url.PLATFORM_SUPPORT_STATUS(chatId)
      : url.SUPPORT_STATUS(chatId),
    body
  );
  return response.data?.data as Chat;
}

export type SupportInboxScope = 'legacy' | 'platform';

export async function addSupportNote(
  chatId: number,
  body: SupportNoteBody,
  scope: SupportInboxScope = 'legacy'
): Promise<ChatMessage> {
  const response: AxiosResponse<Envelope<ChatMessage>> = await axios.post(
    scope === 'platform' ? url.PLATFORM_SUPPORT_NOTE(chatId) : url.SUPPORT_NOTE(chatId),
    body
  );
  return response.data?.data as ChatMessage;
}

export async function sendPlatformSupportReply(chatId: number, body: SendMessageBody) {
  return axios.post(url.PLATFORM_SUPPORT_MESSAGES(chatId), body);
}
export async function uploadPlatformSupportAttachment(chatId: number, form: FormData) {
  return axios.post(`${url.PLATFORM_SUPPORT_DETAIL(chatId)}/attachments`, form);
}
export async function pinPlatformSupportMessage(chatId: number, messageId: number, body: PinMessageBody) {
  return axios.patch(`${url.PLATFORM_SUPPORT_MESSAGES(chatId)}/${messageId}/pin`, body);
}

export async function pinChatMessage(chatId: number, messageId: number, body: PinMessageBody) {
  return axios.patch(`/chats/${chatId}/messages/${messageId}/pin`, body);
}
